import { useEffect, useState } from 'react'
import { Button, Stack, TableCell, TableRow } from '@mui/material'
import { Item } from './snippets'
import { Box, padding, useTheme } from '@mui/system'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import { focusElemnt, getLocalData, navigation3 } from './helper'
import { useAtom } from 'jotai'
import VirtualizedTable from './virtualisedTable'
import { divideInNIntegers, withPercentWidths, withPxWidths } from './mathHelper'
import { exportToExcel } from './hook'
import { CargoRoleRightsAtom } from './jotaiState/commonStateAtoms'
import { xlCol } from './constants'
import useToken from '../helpers/useTokens'
import { makeTable } from '../content/Reports/analysisReport/pdfAutotable'
import { lrDetailPdfFooter } from '../content/AcountingReport/common/AcountingConstants'
import { fixColSpan } from '../content/Reports/common/reportUtils'
import AppsIcon from '@mui/icons-material/Apps'
import ManageColumns from './ManageColumnsUI'
import { modalAtom } from './jotaiState/globalStateAtoms'
import { callAddFontRobotoBold } from './font/Roboto-bold'
import { callAddFontRobotoMedium } from './font/Roboto-Medium-normal'

const currentDateTime = moment(new Date()).format('DD-MMM-YYYY HH:mm')

export const MultiTableComponent = (props) => {
    const theme = useTheme()
    const [modal, setModal] = useAtom(modalAtom)
    const { tableHeaderColor } = theme

    const {
        tables = [], // [{ mainHeader, tableData, excelTableData, criteriaHeaders, criteriaData, reportName }]
        reportName = '',
        hideControls = false,
        hideHeader = false,
        isShowPdfPrint = true,
        isShowManageColumn = false,
        manageColumnID,
    } = props

    const [cargoRoleRights] = useAtom(CargoRoleRightsAtom)
    const { Table1: RoleRightTable1 = [] } = cargoRoleRights
    const [roleRightsData] = RoleRightTable1
    const { AllowExportExcel = 1, AllowPrintandPdf = 1 } = { ...roleRightsData }

    const { loginData } = useToken()
    const [searchResult, setSearchResult] = useState(true)

    // ---------------- Manage Column (ONLY FIRST TABLE) ----------------
    const [manageColumn, setManageColumn] = useState([])
    const [columns, setColumns] = useState([])
    const visibleColumns = columns.length ? columns : tables[0]?.mainHeader
    const setColumn = async () => {
        const savedColumns = await getLocalData(manageColumnID)
        let defaultColumns = savedColumns?.length ? savedColumns : tables[0]?.mainHeader || []
        setManageColumn(defaultColumns)
        setColumns(isShowManageColumn ? defaultColumns.filter((c) => c.isSelected) : defaultColumns)
    }

    useEffect(() => {
        setColumn()
    }, [manageColumnID, tables])

    const getColumns = (tbl, index) => {
        return index === 0 ? (columns.length ? columns : tbl.mainHeader) : tbl.mainHeader
    }
    const prepareExcel = () => {
        const headersList = [];
        const dataList = [];
        headersList.push([
            { field: 'reportName', header: `Report Name: ${reportName}` },
            { field: 'companyName', header: `Company Name: ${loginData?.strCompanyName || ''}` }
        ]);
        dataList.push([{}]);
        const firstTable = tables[0];
        if (firstTable?.criteriaData?.length) {
            headersList.push(firstTable.criteriaHeaders || []);
            dataList.push(firstTable.criteriaData || []);
        }
        tables.forEach((tbl, idx) => {
            const cols = getColumns(tbl, idx);

            const mainHeaders = cols.map(col => ({
                field: col.field,
                header: col.exportHead?.replace(/\n/g, ' '),
                textAlign: col.align
            }));
            headersList.push(mainHeaders);
            dataList.push(tbl.excelTableData?.length ? tbl.excelTableData : tbl.tableData);
        });

        return { headersList, dataList };
    };
    const printClick = (isSave) => {
        jsPDF.API.events.push(['addFonts', callAddFontRobotoBold]);
        jsPDF.API.events.push(['addFonts', callAddFontRobotoMedium]);

        const orientation = visibleColumns.length > 14 ? 'l' : 'p';
        const format = orientation === 'l' ? [1810, 1280] : [1280, 1810];
        const pdfFontSize = 1;
        let totalPagesExp = '{total_pages_count_string}';

        const doc = new jsPDF({ orientation, format, unit: 'px', compress: true });

        doc.setProperties({
            title: `${reportName}.pdf`,
            subject: `${reportName}`,
            author: 'Maven Tech Labs Pvt. Ltd.',
            keywords: 'maven tech, Cargo system, Receive',
            creator: 'MTLPL',
        });

        const pageTop = 120;
        const didDrawPage = (data) => {
            // HEADER
            doc.setTextColor(40);
            doc.addImage('/images/logo-66.png', 'JPEG', 30, 50, 50, 50);
            doc.setFontSize(26).setFont('Roboto', 'bold');
            doc.setTextColor(16, 79, 162);
            doc.text(`Cargo System | ${reportName}`, 90, 80);
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(22).setFont('Roboto', 'bold');
            doc.text(
                `Company Name :${loginData.strCompanyName}`,
                doc.internal.pageSize.width - data.settings.margin.left,
                80,
                'right'
            );
            lrDetailPdfFooter(data, loginData, totalPagesExp);
        };

        let startY = pageTop;

        tables.forEach((tbl, idx) => {
            if (idx === 0 && tbl.criteriaData?.length) {
                const headerColumns = (tbl.criteriaHeaders || []).map((col) => ({
                    header: col.header,
                    dataKey: col.field,
                    xlCol: col.xlCol || xlCol,
                }));

                makeTable(
                    doc,
                    autoTable,
                    headerColumns,
                    tbl.criteriaData || [],
                    pdfFontSize,
                    tbl.reportName || reportName,
                    loginData,
                    'everyPage',
                    didDrawPage,
                    startY,
                    false
                );
                startY = doc.lastAutoTable.finalY + 10;
            }

            // Main tables
            const visibleCols = getColumns(tbl, idx);
            const pdfColumns = withPxWidths(visibleCols, format[0] - 60).map((col) => ({
                header: col.exportHead,
                dataKey: col.field,
                xlCol: col.xlCol,
                width: col.width || 0,
            }));

            // Print table name above main table
            if (tbl.reportName) {
                doc.setFontSize(16);
                doc.setFont('Roboto', 'bold');
                doc.setTextColor(0, 0, 0);

                const tableMarginLeft = 40;
                const textDims = doc.getTextDimensions(tbl.reportName);
                if (startY + textDims.h + 10 > doc.internal.pageSize.height - 50) {
                    doc.addPage();
                    startY = pageTop; // reset Y after header
                }
                doc.text(tbl.reportName, tableMarginLeft, startY);
                startY += textDims.h + 5; // dynamic spacing after table name
            }
            makeTable(
                doc,
                autoTable,
                pdfColumns,
                tbl.tableData,
                pdfFontSize,
                tbl.reportName || reportName,
                loginData,
                hideHeader ? 'never' : 'everyPage',
                didDrawPage,
                startY,
                false,
            );
            startY = doc.lastAutoTable.finalY + 10;
        });

        doc.setFont('Roboto');
        if (typeof doc.putTotalPages === 'function') {
            doc.putTotalPages(totalPagesExp);
        }
        if (isSave) {
            doc.save(`${reportName} ${currentDateTime}.pdf`);
        }
        setTimeout(() => {
            const pdfUrl = doc.output('bloburl');
            document.getElementById('multiPdf').data = pdfUrl;
            focusElemnt('backBtn');
        }, 1000);
    };
    const headerContent = (columns) => {
        let viewHeader = withPercentWidths(columns)

        return (
            <TableRow>
                {viewHeader.map((item) => {
                    return (
                        <TableCell
                            key={item.field}
                            style={{
                                backgroundColor: tableHeaderColor?.isHeadingColor,
                                color: '#444',
                                fontSize: '12px',
                                lineHeight: '16px',
                                letterSpacing: '0.24px',
                                fontWeight: '600',
                                whiteSpace: 'pre-line',
                                padding: '6px 8px',
                                width: item.width,
                                textAlign: item.align || 'left',
                            }}
                        >
                            {item?.header}
                        </TableCell>
                    )
                })}
            </TableRow>
        )
    }

    const DisPatchBody = ({ row, index, tableHeaderColor, columnDefs }) => {
        if (!row?.dataType) return null

        const getStyles = (field, align) => {
            const common = {
                minHeight: '36px',
                fontSize: '12px',
                color: '#444',
                padding: '6px 8px',
            }

            if (row.dataType === 'header') {
                return {
                    ...common,
                    fontWeight: '700',
                    backgroundColor: tableHeaderColor?.isHeadingColor,
                    textAlign: align,
                }
            }

            if (row.dataType === 'entry') {
                return {
                    ...common,
                    fontWeight: '500',
                    textAlign: align || 'left',
                    height: '36px',
                    whiteSpace: 'pre-line',
                }
            }

            if (row.dataType === 'subHeader') {
                return {
                    ...common,
                    fontWeight: '600',
                    backgroundColor: tableHeaderColor?.isSubHeadingColor,
                    textAlign: row.align?.[field] || 'left',
                    position: 'sticky',
                }
            }

            if (row.dataType === 'total') {
                return {
                    ...common,
                    fontWeight: '600',
                    backgroundColor: tableHeaderColor?.isTotalColor,
                    textAlign: row.align?.[field] || 'right',
                }
            }
        }

        return (
            <>
                {(() => {
                    let skip = 0

                    return columnDefs.map((item, i) => {
                        if (skip > 0) {
                            skip--
                            return null
                        }

                        const colSpan = row.colSpan?.[item.field] || 1
                        skip = colSpan - 1

                        return (
                            <TableCell
                                className="reportTD"
                                key={item.field + i + index}
                                colSpan={colSpan}
                                style={getStyles(item.field, item.align)}
                            >
                                {row[item.field]}
                            </TableCell>
                        )
                    })
                })()}
            </>
        )
    }
    const bodyContent = (cols) => (index, obj) => {
        return (
            <DisPatchBody
                row={obj}
                index={index}
                tableHeaderColor={tableHeaderColor}
                columnDefs={cols}
            />
        )
    }
    const onSaveColumns = (items) => {
        setManageColumn(items)
        setColumns(items.filter((col) => col.isSelected))
        localStorage.setItem(manageColumnID, JSON.stringify(items))
    }

    return (
        <>
            <Stack style={{ margin: '10px' }}>
                <Item
                    className="userReport"
                    style={{ padding: 0, display: hideControls ? 'none' : 'flex' }}
                >
                    <Item
                        className="userReport-heading"
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        Report For: {reportName}

                        {isShowManageColumn && (
                            <Box
                                style={{
                                    height: '32px',
                                    display: 'flex',
                                    padding: '8px 12px',
                                    gap: '6px',
                                }}
                                id="dispatchReportManageColumn"
                                tabIndex={0}
                                onClick={() => setModal('ShowManageColumn')}
                            >
                                <span style={{ display: 'flex', margin: '-3px' }}>
                                    <AppsIcon />
                                </span>
                                <span style={{ fontWeight: 'bold' }}>
                                    MANAGE COLUMNS
                                </span>
                            </Box>
                        )}
                    </Item>

                    <Item className="userReport-buttons" style={{ paddingRight: 0 }}>
                        {searchResult ? (
                            <>
                                <Box>
                                    <Button
                                        variant="outlined"
                                        className="userWisereporttembtn"
                                        disabled={!AllowExportExcel}
                                        onClick={() => {
                                            const { headersList, dataList } = prepareExcel();
                                            exportToExcel({
                                                fileName: `${reportName} ${currentDateTime}.xlsx`,
                                                sheetName: reportName,
                                                headersList,
                                                dataList,
                                            });
                                        }}
                                    >
                                        Export to Excel
                                    </Button>
                                </Box>

                                {isShowPdfPrint && (
                                    <>
                                        <Box>
                                            <Button
                                                variant="outlined"
                                                className="userWisereporttembtn"
                                                onClick={() => {
                                                    setSearchResult(false)
                                                    printClick(true)
                                                }}
                                                disabled={!AllowPrintandPdf}
                                            >
                                                Export to Pdf
                                            </Button>
                                        </Box>

                                        <Box>
                                            <Button
                                                variant="outlined"
                                                className="userWisereporttembtn"
                                                onClick={() => {
                                                    setSearchResult(false)
                                                    printClick(false)
                                                }}
                                                disabled={!AllowPrintandPdf}
                                            >
                                                Print
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </>
                        ) : (
                            <Box>
                                <Button
                                    id="backBtn"
                                    variant="outlined"
                                    className="userWisereporttembtn"
                                    onClick={() => setSearchResult(true)}
                                >
                                    Back
                                </Button>
                            </Box>
                        )}
                    </Item>
                </Item>
            </Stack>
            <Stack>
                {searchResult ? (
                    tables.map((tbl, idx) => {
                        const cols = getColumns(tbl, idx)

                        return (
                            <Box key={idx} style={{ marginBottom: '20px', padding: '0px 10px' }}>
                                <Box sx={{ fontWeight: '600', padding: '10px 0px' }}>{tbl.reportName}</Box>

                                <VirtualizedTable
                                    tableData={tbl.tableData}
                                    headerContent={hideHeader ? false : () => headerContent(cols)}
                                    rowContent={bodyContent(cols)}
                                    tableStyle={{ tableLayout: 'auto', width: '100%' }}
                                    autoResize={true}
                                />
                            </Box>
                        )
                    })
                ) : (
                    <object id="multiPdf" type="application/pdf" width="100%" height="500" />
                )}
            </Stack>

            {modal === 'ShowManageColumn' && (
                <ManageColumns headers={manageColumn} onSave={onSaveColumns} updateModal={setModal} />
            )}
        </>
    )
}